import React, { useMemo } from 'react';
import { AppButton, Box, Text } from '@common-fe/common-fe';

import { PlanStatuses } from '@/modules/plan/plan.types';

import { useCheckPlanReactivationEligibility } from '../../PlanReactivation/queries';

interface Props {
  planId?: string | number;
  planStatus?: PlanStatuses;
  planName?: string;
  onReactivatePlan: () => void;
}

export const ReactivationDangerZoneCard: React.FC<Props> = ({
  planId,
  planStatus,
  onReactivatePlan,
  planName,
}) => {
  const { isPlanEligibleForReactivation } = useCheckPlanReactivationEligibility([planId as string]);

  const isPendingReactivation = useMemo(
    () => planStatus === PlanStatuses.PENDING_REACTIVATION,
    [planStatus]
  );

  const title = useMemo(() => {
    if (isPendingReactivation) return 'Reactivation in Progress';
    if (!isPlanEligibleForReactivation) return 'Reactivation is Disabled';
    return 'Reactivate';
  }, [isPlanEligibleForReactivation, isPendingReactivation]);

  const content = useMemo(() => {
    if (isPendingReactivation) {
      return (
        <Box margin={{ top: 'spacing12' }} direction="row">
          <Text color="textBody" textAlign="center" style={{ whiteSpace: 'pre-line' }}>
            {`${planName} is being reactivated.\nThis can take a few moments to complete.`}
          </Text>
        </Box>
      );
    }

    if (!isPlanEligibleForReactivation) {
      return (
        <Box margin={{ top: 'spacing12' }} direction="row">
          <Text color="textBody" textAlign="center">
            Only plans with remaining dates of coverage can be reactivated
          </Text>
        </Box>
      );
    }

    return (
      <Box fill margin={{ top: 'spacing24' }} direction="column">
        <AppButton
          testId="reactivate-plan"
          size="L"
          onClick={onReactivatePlan}
          width="100%"
        >
          Reactivate Plan
        </AppButton>
      </Box>
    );
  }, [isPlanEligibleForReactivation, isPendingReactivation, planName, onReactivatePlan]);

  return (
    <Box
      data-testid="plan-reactivation-danger-zone"
      align="center"
      width="50%"
      pad="spacing24"
      round="container1Round"
      direction="column"
      elevation="default"
      background="canvas"
      margin={{ right: 'spacing8' }}
    >
      <Text size="2xl" color="textBody" weight={700}>
        {title}
      </Text>

      {content}
    </Box>
  );
};
